import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DatePicker,
  Divider,
  Drawer,
  Empty,
  Flex,
  Input,
  Modal,
  Select,
  Spin,
  Tag,
  Timeline,
  Upload,
  notification,
} from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { artifactApi } from '../api';
import { userRole, employeeId } from '../../GetRole.utils';
import {
  ACCEPTED_FILE_TYPES,
  ARTIFACT_STATUS,
  MAX_FILE_SIZE_MB,
  TRANSITION_LABEL,
  allowedTransitions,
} from './artifactConstants';

const { TextArea } = Input;

const notifyError = (err, fallback) =>
  notification.error({ message: err?.response?.data?.message || fallback });

const statusLabel = (status) => ARTIFACT_STATUS[status]?.label || status;

// One line per activity entry, oldest status names translated to labels.
const describeActivity = (entry) => {
  const who = entry.name || entry.empCode || 'System';
  if (entry.action === 'uploaded') {
    return `${who} uploaded a new version (${statusLabel(entry.toStatus)})`;
  }
  if (entry.action === 'status') {
    return `${who} moved ${statusLabel(entry.fromStatus)} → ${statusLabel(entry.toStatus)}`;
  }
  if (entry.action === 'updated') return `${who} updated details`;
  return `${who} commented`;
};

const ArtifactDrawer = ({ artifactId, employees, onClose, onChanged }) => {
  const readOnly = userRole === 'finance';
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadForm, setUploadForm] = useState({});
  const [comment, setComment] = useState('');
  const [naOpen, setNaOpen] = useState(false);
  const [naReason, setNaReason] = useState('');

  const loadDetail = useCallback(async () => {
    if (!artifactId) return;
    setLoading(true);
    try {
      const response = await artifactApi.getArtifact(artifactId);
      setDetail(response.data);
    } catch (err) {
      notifyError(err, 'Could not load the artifact.');
    } finally {
      setLoading(false);
    }
  }, [artifactId]);

  useEffect(() => {
    setDetail(null);
    setFileList([]);
    setUploadForm({});
    setComment('');
    loadDetail();
  }, [loadDetail]);

  const afterChange = async (successMessage) => {
    notification.success({ message: successMessage });
    await loadDetail();
    onChanged();
  };

  const updateField = async (field, value) => {
    setSaving(true);
    try {
      await artifactApi.updateArtifact(artifactId, { [field]: value, empCode: employeeId });
      await afterChange('Artifact updated.');
    } catch (err) {
      notifyError(err, 'Could not update the artifact.');
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status, extra = {}) => {
    setSaving(true);
    try {
      await artifactApi.updateStatus(artifactId, { status, empCode: employeeId, ...extra });
      await afterChange(`Status changed to ${statusLabel(status)}.`);
    } catch (err) {
      notifyError(err, 'Could not change the status.');
    } finally {
      setSaving(false);
    }
  };

  const submitNotApplicable = async () => {
    await changeStatus('notapplicable', { naReason: naReason.trim() });
    setNaOpen(false);
    setNaReason('');
  };

  const uploadVersion = async () => {
    const artifact = detail.artifact;
    const file = fileList[0];
    if (!file) return;
    if (artifact.isRecurring && !uploadForm.periodStart) {
      notification.warning({ message: 'Pick the week this report covers.' });
      return;
    }
    if (artifact.isPerResource && !uploadForm.resourceEmpCode) {
      notification.warning({ message: 'Pick the team member who signed.' });
      return;
    }

    const obj = { empCode: employeeId };
    if (uploadForm.status) obj.status = uploadForm.status;
    if (uploadForm.notes) obj.notes = uploadForm.notes;
    if (uploadForm.periodStart) obj.periodStart = uploadForm.periodStart;
    if (uploadForm.resourceEmpCode) obj.resourceEmpCode = uploadForm.resourceEmpCode;

    const formdata = new FormData();
    formdata.append('file', file);
    formdata.append('obj', JSON.stringify(obj));

    setSaving(true);
    try {
      await artifactApi.uploadVersion(artifactId, formdata);
      setFileList([]);
      setUploadForm({});
      await afterChange('New version uploaded.');
    } catch (err) {
      notifyError(err, 'Upload failed.');
    } finally {
      setSaving(false);
    }
  };

  const downloadVersion = async (version) => {
    try {
      const blob = await artifactApi.downloadVersion(version.versionId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = version.fileName;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      notifyError(err, 'Download failed.');
    }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      await artifactApi.addComment(artifactId, { comment: comment.trim(), empCode: employeeId });
      setComment('');
      await loadDetail();
    } catch (err) {
      notifyError(err, 'Could not add the comment.');
    } finally {
      setSaving(false);
    }
  };

  const artifact = detail?.artifact;
  const employeeOptions = (employees || []).map((emp) => ({
    value: emp.empCode,
    label: `${emp.name} (${emp.empCode})`,
  }));
  const uploadStatusOptions = ['draft', 'inreview', 'approved', 'signed']
    .filter((status) => status !== 'signed' || artifact?.requiresSignature)
    .map((status) => ({ value: status, label: statusLabel(status) }));

  return (
    <Drawer
      open={!!artifactId}
      onClose={onClose}
      width={560}
      destroyOnClose
      title={
        artifact ? (
          <Flex vertical gap={4}>
            <span style={{ fontSize: 12, color: '#5B6270', fontWeight: 400 }}>
              Project management · {artifact.phase}
            </span>
            <span>{artifact.name}</span>
            <Flex gap={4}>
              <Tag color={ARTIFACT_STATUS[artifact.status]?.color}>
                {statusLabel(artifact.status)}
              </Tag>
              {artifact.isOverdue && <Tag color="error">Overdue</Tag>}
            </Flex>
          </Flex>
        ) : (
          'Artifact'
        )
      }
    >
      {loading && !artifact ? (
        <Flex justify="center" style={{ padding: 48 }}>
          <Spin />
        </Flex>
      ) : artifact ? (
        <Flex vertical gap={20}>
          {artifact.status === 'notapplicable' && artifact.naReason && (
            <div style={{ padding: 12, background: '#F4F5F7', borderRadius: 8 }}>
              Not applicable: {artifact.naReason}
            </div>
          )}

          {!readOnly && allowedTransitions(artifact).length > 0 && (
            <Flex gap={8} wrap="wrap">
              {allowedTransitions(artifact).map((status) => (
                <Button
                  key={status}
                  loading={saving}
                  type={['approved', 'signed', 'inreview'].includes(status) ? 'primary' : 'default'}
                  onClick={() =>
                    status === 'notapplicable' ? setNaOpen(true) : changeStatus(status)
                  }
                >
                  {TRANSITION_LABEL[status]}
                </Button>
              ))}
            </Flex>
          )}

          <Flex vertical gap={12}>
            <h4 style={{ margin: 0 }}>Details</h4>
            <Flex vertical gap={4}>
              <label htmlFor="artifact-owner">Owner</label>
              <Select
                id="artifact-owner"
                showSearch
                allowClear
                disabled={readOnly || saving}
                placeholder="Assign an owner"
                optionFilterProp="label"
                options={employeeOptions}
                value={artifact.ownerEmpCode || undefined}
                onChange={(value) => updateField('ownerEmpCode', value || null)}
              />
            </Flex>
            <Flex vertical gap={4}>
              <label htmlFor="artifact-due">
                {artifact.isRecurring ? 'First report due' : 'Due date'}
              </label>
              <DatePicker
                id="artifact-due"
                format="DD/MM/YYYY"
                disabled={readOnly || saving}
                value={artifact.dueDate ? dayjs(artifact.dueDate) : null}
                onChange={(date) =>
                  updateField('dueDate', date ? date.format('YYYY-MM-DD') : null)
                }
              />
            </Flex>
          </Flex>

          {!readOnly && (
            <>
              <Divider style={{ margin: 0 }} />
              <Flex vertical gap={12}>
                <h4 style={{ margin: 0 }}>
                  {artifact.isRecurring
                    ? 'Upload weekly report'
                    : artifact.isPerResource
                    ? 'Upload a signed NDA'
                    : 'Upload new version'}
                </h4>
                <Upload.Dragger
                  accept={ACCEPTED_FILE_TYPES}
                  maxCount={1}
                  fileList={fileList}
                  beforeUpload={(file) => {
                    if (file.size / 1024 / 1024 >= MAX_FILE_SIZE_MB) {
                      notification.error({
                        message: `File must be smaller than ${MAX_FILE_SIZE_MB} MB.`,
                      });
                      return Upload.LIST_IGNORE;
                    }
                    setFileList([file]);
                    return false;
                  }}
                  onRemove={() => setFileList([])}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-text">Click or drag a file here</p>
                  <p className="ant-upload-hint">
                    PDF, Word, PowerPoint or Excel · up to {MAX_FILE_SIZE_MB} MB
                  </p>
                </Upload.Dragger>
                {artifact.isRecurring && (
                  <DatePicker
                    picker="week"
                    placeholder="Week covered"
                    value={uploadForm.periodStart ? dayjs(uploadForm.periodStart) : null}
                    onChange={(date) =>
                      setUploadForm((prev) => ({
                        ...prev,
                        // server tracks ISO weeks (Mon–Sun); the picker's locale week starts Sunday
                        periodStart: date
                          ? date.startOf('week').add(date.startOf('week').day() === 0 ? 1 : 0, 'day').format('YYYY-MM-DD')
                          : undefined,
                      }))
                    }
                  />
                )}
                {artifact.isPerResource && (
                  <Select
                    showSearch
                    placeholder="Team member who signed"
                    optionFilterProp="label"
                    options={employeeOptions}
                    value={uploadForm.resourceEmpCode}
                    onChange={(value) =>
                      setUploadForm((prev) => ({ ...prev, resourceEmpCode: value }))
                    }
                  />
                )}
                <Select
                  allowClear
                  placeholder="Status after upload (default: Draft)"
                  options={uploadStatusOptions}
                  value={uploadForm.status}
                  onChange={(value) => setUploadForm((prev) => ({ ...prev, status: value }))}
                />
                <TextArea
                  rows={2}
                  placeholder="What changed in this version? (optional)"
                  value={uploadForm.notes}
                  onChange={(e) =>
                    setUploadForm((prev) => ({ ...prev, notes: e.target.value }))
                  }
                />
                <Button
                  type="primary"
                  disabled={fileList.length === 0}
                  loading={saving}
                  onClick={uploadVersion}
                >
                  Upload
                </Button>
              </Flex>
            </>
          )}

          <Divider style={{ margin: 0 }} />
          <Flex vertical gap={8}>
            <h4 style={{ margin: 0 }}>Versions</h4>
            {detail.versions.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No files yet" />
            ) : (
              detail.versions.map((version) => (
                <Flex
                  key={version.versionId}
                  align="center"
                  gap={12}
                  style={{ padding: '8px 12px', border: '1px solid #E3E5EA', borderRadius: 8 }}
                >
                  <b style={{ width: 32 }}>v{version.versionNo}</b>
                  <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {version.fileName}
                    </span>
                    <span style={{ fontSize: 12, color: '#5B6270' }}>
                      {version.uploadedByName || version.uploadedBy || 'Unknown'} ·{' '}
                      {dayjs(version.createdAt).format('DD/MM/YYYY HH:mm')}
                      {version.periodStart &&
                        ` · week of ${dayjs(version.periodStart).format('DD MMM')}`}
                      {version.resourceName && ` · ${version.resourceName}`}
                    </span>
                    {version.notes && <span style={{ fontSize: 12 }}>{version.notes}</span>}
                  </Flex>
                  <Button
                    icon={<DownloadOutlined />}
                    aria-label={`Download version ${version.versionNo}`}
                    onClick={() => downloadVersion(version)}
                  />
                </Flex>
              ))
            )}
          </Flex>

          <Divider style={{ margin: 0 }} />
          <Flex vertical gap={8}>
            <h4 style={{ margin: 0 }}>Activity & comments</h4>
            <Flex gap={8}>
              <TextArea
                rows={2}
                placeholder="Add a comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              <Button onClick={submitComment} loading={saving} disabled={!comment.trim()}>
                Post
              </Button>
            </Flex>
            {detail.activity.length === 0 ? (
              <span style={{ color: '#5B6270' }}>No activity yet.</span>
            ) : (
              <Timeline
                style={{ marginTop: 12 }}
                items={detail.activity.map((entry) => ({
                  key: entry.activityId,
                  color: entry.action === 'comment' ? 'gray' : 'blue',
                  children: (
                    <Flex vertical>
                      <span>{describeActivity(entry)}</span>
                      {entry.comment && (
                        <span style={{ color: '#3F4652' }}>{entry.comment}</span>
                      )}
                      <span style={{ fontSize: 12, color: '#5B6270' }}>
                        {dayjs(entry.createdAt).format('DD/MM/YYYY HH:mm')}
                      </span>
                    </Flex>
                  ),
                }))}
              />
            )}
          </Flex>
        </Flex>
      ) : null}

      <Modal
        title="Mark as not applicable"
        open={naOpen}
        onCancel={() => setNaOpen(false)}
        onOk={submitNotApplicable}
        okText="Mark not applicable"
        okButtonProps={{ disabled: naReason.trim().length < 3, loading: saving }}
      >
        <p>Explain why this artifact isn't needed for this project.</p>
        <TextArea rows={3} value={naReason} onChange={(e) => setNaReason(e.target.value)} />
      </Modal>
    </Drawer>
  );
};

export default ArtifactDrawer;
